import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const REGIONS = ["Americas", "Europe", "Asia Pacific", "Remote / Global"];
const SENIORITIES = ["Mid", "Senior", "Executive"];
const TYPES = [
    { value: "permanent", label: "Permanent" },
    { value: "temporary", label: "Temporary" },
    { value: "contract", label: "Contract" },
];

export default function PostMission() {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({
        title: "", company: "", location: "", region: REGIONS[0],
        description: "", skills: "",
        salary_min: "", salary_max: "", referral_bounty: "",
        seniority: SENIORITIES[0], mission_type: "permanent",
        poster_email: "", poster_name: "",
    });

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    async function onSubmit(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                salary_min: parseInt(form.salary_min || "0", 10),
                salary_max: parseInt(form.salary_max || "0", 10),
                referral_bounty: parseInt(form.referral_bounty || "0", 10),
                skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
            };
            await api.post("/exchange/missions", payload);
            setSubmitted(true);
            toast.success("Mission received — review pending.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    }

    if (submitted) {
        return (
            <div data-testid="mission-submitted" className="mx-auto max-w-2xl px-6 py-24 text-center">
                <div className="caption text-[var(--brand-accent)]">Mission Received</div>
                <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA]">In review.</h1>
                <p className="mt-5 text-[#A3A3A3]">
                    Our admiralty will publish your Mission to the Global Opportunity Exchange within 24 hours.
                    You'll hear from us at <span className="text-[#FAFAFA]">{form.poster_email}</span>.
                </p>
                <button
                    onClick={() => navigate("/")}
                    className="mt-10 rounded-sm border border-white/10 px-6 py-3 text-sm text-[#FAFAFA] hover:bg-white/5"
                >
                    Return to The Foundry
                </button>
            </div>
        );
    }

    return (
        <div data-testid="post-mission-page" className="mx-auto max-w-3xl px-6 py-16 md:px-10">
            <div className="caption text-[var(--brand-accent)]">Global Opportunity Exchange</div>
            <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA] sm:text-6xl">
                Post a Mission
            </h1>
            <p className="mt-4 max-w-xl text-[#A3A3A3]">
                External companies, post permanent or temporary roles. Our Fleet of Privateers will surface qualified candidates.
                Set a Referral Bounty for permanent placements; pay only when filled.
            </p>

            <form onSubmit={onSubmit} className="mt-10 grid gap-5 md:grid-cols-2">
                <Field label="Mission title" testId="pm-title" value={form.title} onChange={update("title")} required wide />
                <Field label="Company" testId="pm-company" value={form.company} onChange={update("company")} required />
                <Field label="Location" testId="pm-location" value={form.location} onChange={update("location")} required />
                <Select label="Region" testId="pm-region" value={form.region} onChange={update("region")} options={REGIONS} />
                <Select label="Seniority" testId="pm-seniority" value={form.seniority} onChange={update("seniority")} options={SENIORITIES} />
                <Select label="Mission type" testId="pm-mission_type" value={form.mission_type} onChange={update("mission_type")} options={TYPES.map(t => t.value)} labels={Object.fromEntries(TYPES.map(t => [t.value, t.label]))} />
                <Field label="Salary min (USD)" testId="pm-salary_min" type="number" value={form.salary_min} onChange={update("salary_min")} required />
                <Field label="Salary max (USD)" testId="pm-salary_max" type="number" value={form.salary_max} onChange={update("salary_max")} required />
                <Field label="Referral Bounty (USD) — paid on permanent placement" testId="pm-referral_bounty" type="number" value={form.referral_bounty} onChange={update("referral_bounty")} required wide placeholder="e.g. 5000" />
                <TextArea label="Skills required (comma-separated)" testId="pm-skills" value={form.skills} onChange={update("skills")} rows={2} required />
                <TextArea label="Mission description" testId="pm-description" value={form.description} onChange={update("description")} rows={6} required minLength={20} />

                <div className="md:col-span-2 mt-4 border-t border-white/5 pt-6">
                    <div className="caption text-[var(--brand-accent)]">Your contact</div>
                </div>
                <Field label="Your name" testId="pm-poster_name" value={form.poster_name} onChange={update("poster_name")} required />
                <Field label="Your email" testId="pm-poster_email" type="email" value={form.poster_email} onChange={update("poster_email")} required />

                <div className="md:col-span-2 mt-4">
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="pm-submit"
                        className="rounded-sm px-8 py-3.5 text-sm font-medium transition-all duration-300 disabled:opacity-50"
                        style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                    >
                        {submitting ? "Submitting…" : "Submit Mission for Review"}
                    </button>
                    <p className="mt-3 caption text-[#737373]">
                        We review all Missions within 24 hours. No payment until a placement is made.
                    </p>
                </div>
            </form>
        </div>
    );
}

function Field({ label, testId, wide, ...rest }) {
    return (
        <div className={wide ? "md:col-span-2" : undefined}>
            <label className="caption text-[#737373]">{label}</label>
            <input data-testid={testId} {...rest}
                className="mt-2 w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] placeholder:text-white/30 focus:border-[var(--brand-accent)] focus:outline-none"
            />
        </div>
    );
}
function TextArea({ label, testId, rows = 4, ...rest }) {
    return (
        <div className="md:col-span-2">
            <label className="caption text-[#737373]">{label}</label>
            <textarea data-testid={testId} rows={rows} {...rest}
                className="mt-2 w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] placeholder:text-white/30 focus:border-[var(--brand-accent)] focus:outline-none"
            />
        </div>
    );
}
function Select({ label, testId, value, onChange, options, labels }) {
    return (
        <div>
            <label className="caption text-[#737373]">{label}</label>
            <select data-testid={testId} value={value} onChange={onChange}
                className="mt-2 w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] focus:border-[var(--brand-accent)] focus:outline-none"
            >
                {options.map((o) => <option key={o} value={o}>{labels ? labels[o] : o}</option>)}
            </select>
        </div>
    );
}
